import React, { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, Plus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import { fetchCalendar } from "../features/calendar/slice-extensions/calendar-slice-extension";
import { selectCalendarItems, selectCalendarStatus } from "../features/calendar/calendar-slice";
import { OccurrenceStatus } from "../features/sessions/models/OccurrenceStatus";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { formatDateDisplay, formatTimeDisplay } from "../features/sessions/utils/DisplayMappers";
import { addDays, toYmd } from "../features/calendar/utils/calendar-range";
import { ROUTES } from "../routes";

/**
 * Phone-first attendance flow, step 1: the day's occurrences. Defaults to today;
 * tapping an occurrence opens its one-tap marking screen.
 */
const AttendanceDayPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const today = toYmd(new Date());
  const date = searchParams.get("date") ?? today;

  const items = useAppSelector(selectCalendarItems);
  const status = useAppSelector(selectCalendarStatus);

  useEffect(() => {
    dispatch(fetchCalendar({ from: date, to: date }));
  }, [dispatch, date]);

  const dayItems = useMemo(
    () =>
      items
        .filter((i) => i.date === date)
        .sort((a, b) => a.time.localeCompare(b.time)),
    [items, date]
  );

  const goTo = (target: string) =>
    setSearchParams(target === today ? {} : { date: target }, { replace: false });
  const shift = (days: number) => goTo(toYmd(addDays(new Date(`${date}T00:00:00`), days)));

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[1280px] 2xl:max-w-[1760px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6">
              <h1>{t("sessions.attendance.title")}</h1>

              {/* Day navigation — thumb-sized targets */}
              <div className="flex items-center justify-between gap-2">
                <button type="button" onClick={() => shift(-1)} aria-label={t("sessions.attendance.previousDay")} className="btn btn-secondary px-3 py-2">
                  <ChevronLeft size={20} />
                </button>
                <div className="text-center">
                  <p className="font-semibold text-grey-10 dark:text-grey-90">{formatDateDisplay(date)}</p>
                  {date !== today && (
                    <button type="button" onClick={() => goTo(today)} className="text-sm text-primary-40 hover:underline dark:text-primary-90">
                      {t("sessions.attendance.backToToday")}
                    </button>
                  )}
                </div>
                <button type="button" onClick={() => shift(1)} aria-label={t("sessions.attendance.nextDay")} className="btn btn-secondary px-3 py-2">
                  <ChevronRight size={20} />
                </button>
              </div>

              {status === LoadingStatus.Loading && dayItems.length === 0 ? (
                <p className="text-sm text-grey-40 dark:text-grey-60">{t("sessions.sessionLoading")}</p>
              ) : dayItems.length === 0 ? (
                <div className="rounded-2xl border border-grey-90 p-6 text-center dark:border-primary-20">
                  <p className="text-sm text-grey-40 dark:text-grey-60">
                    {t("sessions.attendance.dayEmpty")}
                  </p>
                  {/* Cross-link into the setup flow — an empty day shouldn't dead-end. */}
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                    <Link to={ROUTES.CALENDAR} className="btn btn-secondary">
                      {t("sessions.attendance.openCalendar")}
                    </Link>
                    <Link to={ROUTES.CREATE_SESSION} className="btn btn-primary">
                      <Plus size={18} strokeWidth={2} />
                      <span>{t("sessions.createSession")}</span>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                  {dayItems.map((item) => {
                    const cancelled = item.status === OccurrenceStatus.Cancelled;
                    return (
                      <button
                        key={`${item.bookingId}-${item.date}`}
                        type="button"
                        onClick={() =>
                          navigate(
                            cancelled
                              ? ROUTES.SESSION_DETAILS.replace(":id", item.bookingId)
                              : `${ROUTES.ATTENDANCE}/${item.bookingId}/${item.date}`
                          )
                        }
                        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-grey-90 p-4 text-left hover:bg-grey-95 dark:border-primary-20 dark:hover:bg-primary-20"
                      >
                        <div className="min-w-0">
                          <p className={`font-medium ${cancelled ? "text-grey-40 line-through dark:text-grey-60" : "text-grey-10 dark:text-grey-90"}`}>
                            {formatTimeDisplay(item.time)} · {item.title}
                          </p>
                          <p className="mt-0.5 flex items-center gap-2 text-sm text-grey-30 dark:text-grey-70">
                            <Users size={14} />
                            <span>{item.inviteeCount}</span>
                            {!cancelled && item.visitCount > 0 ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                <Check size={12} />
                                {t("sessions.attendance.recorded")}
                              </span>
                            ) : !cancelled ? (
                              <span className="rounded-full bg-grey-95 px-2 py-0.5 text-xs font-medium text-grey-30 dark:bg-primary-20 dark:text-grey-70">
                                {t("sessions.attendance.notRecorded")}
                              </span>
                            ) : (
                              <span className="text-xs">{t("sessions.occurrenceList.statusCancelled")}</span>
                            )}
                          </p>
                        </div>
                        <ChevronRight size={18} className="shrink-0 text-grey-40 dark:text-grey-60" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AttendanceDayPage;
